/**
 * Town-square route routing for chat Markdown links.
 *
 * The chat Markdown renderer (`client/src/components/Chat/tool-renderers/common.tsx`)
 * historically routes every `/`-prefixed href through React Router `<Link>`. That
 * works for client-app routes (e.g. `/my-artwork`, `/art/:id`, `/billing`) but
 * 404s for routes that live in the town-square app (`/community`, `/contests`,
 * `/verify`).
 *
 * To bridge the boundary, this helper exposes a small whitelist of path prefixes
 * that are owned by the town-square app. Any href whose first path segment matches
 * one of these prefixes is rendered as a full-page <a> to `${baseUrl}${href}`
 * instead of a <Link>. Callers pass `__TOWN_SQUARE_URL__` (the Vite-injected
 * global, see client/src/vite-env.d.ts) as `baseUrl`.
 *
 * To add a new town-square route (e.g. `/artists`, `/art`, `/about`,
 * `/artist-spotlight`, `/verified/...`, `/hashtag/...`):
 *   1. Add the bare first path segment (no leading slash) to TOWN_SQUARE_PATH_PREFIXES.
 *   2. Verify the corresponding `town-square/src/app/[locale]/<segment>/` route exists.
 *   3. Send a relevant Assistant question and click the rendered link to confirm.
 *
 * Detection is anchored to path-segment boundaries: `/community` and
 * `/community/foo` match `community`; `/community-other` does NOT.
 */
export const TOWN_SQUARE_PATH_PREFIXES = ['community', 'community-standards', 'contests', 'verify'] as const;

/**
 * Production fallback origin for the town-square app, used when the
 * Vite-injected `__TOWN_SQUARE_URL__` global is empty or unset (e.g. a
 * misconfigured env). Mirrors the inline fallback the rest of the client
 * already uses — see `AppSidebar.tsx`, `SiteHeader.tsx`, `FlagIndicator.tsx`
 * (`${__TOWN_SQUARE_URL__ || 'https://www.arthelper.com'}/...`) — so chat
 * Markdown links cross the domain to the same origin those nav links target.
 */
export const DEFAULT_TOWN_SQUARE_URL = 'https://www.arthelper.com';

/**
 * Returns true iff `href` is a path that should be routed to the town-square app
 * rather than handled by the client React Router.
 *
 * Only `/`-prefixed paths are considered — absolute URLs (`http://`, `https://`,
 * `mailto:`, etc.) and falsy / non-string values return false.
 */
export function isTownSquarePath(href: string | undefined | null): boolean {
  if (!href || typeof href !== 'string') return false;
  if (!href.startsWith('/')) return false;
  // Strip the leading slash, then take the first path segment (everything before
  // the next '/', '?', or '#'). Anchoring to a segment boundary prevents
  // `/community-other` from accidentally matching `community`.
  const firstSegment = href.slice(1).split(/[/?#]/, 1)[0];
  return (TOWN_SQUARE_PATH_PREFIXES as readonly string[]).includes(firstSegment);
}

/**
 * Builds an absolute URL into the town-square app for a town-square path.
 *
 * Returns:
 *   - null if `href` is not a town-square path (caller should fall back to <Link>).
 *   - Otherwise `${baseUrl}${href}` concatenated as-is, falling back to
 *     DEFAULT_TOWN_SQUARE_URL when `baseUrl` is falsy. This matches the
 *     `${__TOWN_SQUARE_URL__ || 'https://www.arthelper.com'}/...` pattern used
 *     in AppSidebar / SiteHeader / FlagIndicator, so chat Markdown navigation
 *     stays consistent with the rest of the client when the env global is
 *     unset. We do not normalise trailing slashes; production
 *     __TOWN_SQUARE_URL__ does not have one.
 */
export function toTownSquareUrl(href: string | undefined | null, baseUrl: string | undefined | null): string | null {
  if (!isTownSquarePath(href)) return null;
  return `${baseUrl || DEFAULT_TOWN_SQUARE_URL}${href}`;
}
